import { useNavigate } from 'react-router-dom'
import { Star, Zap } from 'lucide-react'
import { logStatsigEvent } from '../lib/statsig'
import { goToEmergencyForm } from '../lib/goToEmergencyForm'

interface MobileBookBarProps {
  plumberId: string
  companyName: string
  rating?: number
  reviewCount?: number
  emergencyAvailable?: boolean
  /** Which profile page the bar sits on, for the Statsig click event. */
  page: 'directory-profile' | 'plumber-profile'
}

/**
 * Sticky bottom CTA bar for plumber profile pages on mobile/tablet. Desktop
 * (lg+) already has the sticky sidebar CTA, so the bar hides there. Sends the
 * visitor into the homepage emergency form — the single booking funnel.
 * Rendered via PageWrapper's bottomBar slot, which pads main to match.
 */
export function MobileBookBar({ plumberId, companyName, rating, reviewCount, emergencyAvailable, page }: MobileBookBarProps) {
  const navigate = useNavigate()
  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)] px-4 pt-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))]">
      <div className="flex items-center gap-3 max-w-lg mx-auto">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{companyName}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {rating !== undefined && rating > 0 && (
              <span className="inline-flex items-center gap-1">
                <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                <span className="font-medium text-gray-700">{rating.toFixed(1)}</span>
                {reviewCount !== undefined && reviewCount > 0 && <span>({reviewCount.toLocaleString()})</span>}
              </span>
            )}
            {emergencyAvailable && (
              <span className="inline-flex items-center gap-0.5 font-semibold text-emergency-600">
                <Zap className="h-3 w-3" /> 24/7
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            logStatsigEvent('plumber_book_cta_clicked', plumberId, { placement: 'mobile-sticky-bar', page })
            goToEmergencyForm(navigate)
          }}
          className="flex-shrink-0 flex items-center justify-center bg-emergency-600 hover:bg-emergency-700 text-white font-bold px-6 py-3 rounded-xl transition-colors text-sm"
        >
          Get help now
        </button>
      </div>
    </div>
  )
}
